import type { CategoryPageData } from './types'
import WhyImage from '@/assets/images/refresh/clinitian-hiring/why.jpg'
import GeneralMedicinePhone from '@/assets/images/general-medicine-phone.png'

import SleepSupportImg from '@/assets/images/refresh/categories/support-cards/sleep-support.webp'
import MenopauseImg from '@/assets/images/refresh/categories/support-cards/menopause.webp'
import MetabolicImg from '@/assets/images/refresh/categories/support-cards/metabolic-health.webp'
import StreesAnxietyImg from '@/assets/images/refresh/categories/support-cards/stress-anxiety.webp'
import NutritionImg from '@/assets/images/refresh/categories/support-cards/nutrition-support.webp'

import WhyImg1 from '@/assets/images/refresh/categories/why-gm/why-1.webp'
import WhyImg2 from '@/assets/images/refresh/categories/why-gm/why-2.webp'

import HeroImg from '@/assets/images/refresh/categories/weight-loss/hero.webp'
import HeroTitleImg1 from '@/assets/images/refresh/categories/weight-loss/hero-tile-1.webp'
import HeroTitleImg2 from '@/assets/images/refresh/categories/weight-loss/hero-tile-2.webp'
import HeroTitleImg3 from '@/assets/images/refresh/categories/weight-loss/hero-tile-3.webp'

import CategoryStatsImg from '@/assets/images/refresh/categories/weight-loss/statistic.webp'

export const weightLossData: CategoryPageData = {
    supportWidget: {
        hideEyebrow: true,
        titleOverride: 'More from General Medicine',
    },
    generalAISm: {
        title: 'Not sure where to start? Let General AI help.',
        body: 'Curious whether a GLP-1, a non-GLP-1 medication, or lifestyle-focused care is right for you? Chat with General AI to explore possible causes of weight gain, understand treatment paths, and get ready for a more productive visit with your clinician.',
    },
    categoryHeader: {
        showPrice: true,
        title: 'Weight loss that works, with ongoing support from clinicians.',
        description:
            '<p>From GLP-1 medications like <b>Zepbound®, Wegovy®, and Ozempic®</b> to broader support for nutrition, sleep, and overall wellbeing, our clinicians tailor the right plan for your body and your goals. Finally, lose weight safely and keep it off.</p>',
        stickyBarLabel: {
            base: 'Get started today',
            sm: 'Start your weight-loss journey today.',
        },
        primaryCta: { label: 'Book a visit', href: '/visit' },
        secondaryCta: { label: 'Check my price', href: '/visit' },
        anchorLinks: [
            { label: 'How it works', href: '#contentAccordion' },
            { label: 'Solutions', href: '#tabbedCardSliderV2' },
            { label: 'Testimonials', href: '#testimonialReviewsV2' },
            { label: 'FAQs', href: '#faqs' },
        ],
        images: [
            { src: HeroImg, alt: 'Weight loss' },
            { src: HeroTitleImg1, alt: 'GLP-1 injection' },
            { src: HeroTitleImg2, alt: 'Medication' },
            { src: HeroTitleImg3, alt: 'Zepbound' },
        ],
    },
    wholeBodyCarousel: {
        title: 'Weight loss comes from whole-body health',
        subtitle:
            'Address the underlying causes of your weight gain. Explore complementary solutions that can influence weight, including those focused on metabolism, sleep, and overall well-being.',
        footerNote:
            'Note: Weight is one important factor in healthy living. According to the American Heart Association, there are 8 important health factors not mentioned above, including: 1) managing blood pressure, 2) managing blood sugar, 3) controlling cholesterol, 4) quitting tobacco, and 5) being more active. <strong>We can help with all of the above.</strong>',
        otherCards: [
            {
                id: 'sleep-support',
                label: 'Sleep support',
                description: 'Treatments for insomnia and disrupted sleep cycles',
                image: SleepSupportImg,
                url: '/store/sleep',
            },
            {
                id: 'menopause',
                label: 'Menopause',
                description: 'Hormone and non-hormone options that address mid-life metabolic shifts',
                image: MenopauseImg,
                url: '/s/menopause-symptom-management-consult/364',
            },
            {
                id: 'metabolic-health',
                label: 'Metabolic health',
                description: 'Labs for thyroid, insulin resistance, and hormonal balance',
                image: MetabolicImg,
                url: '/s/comprehensive-metabolic-panel/500008',
            },
            {
                id: 'stress-anxiety',
                label: 'Stress & anxiety',
                description: 'Behavioral and medical tools for whole-body support',
                image: StreesAnxietyImg,
                url: '/s/anxiety/300302',
            },
            {
                id: 'nutrition-support',
                label: 'Nutrition support',
                description: 'Guidance on how to make the right decisions around your nutritional needs',
                image: NutritionImg,
                url: '/s/weight-gain/300055',
            },
        ],
    },
    getStartedCta: {
        heading: 'Start your clinician-led weight-loss journey today.',
        body: 'Whether you want access to a GLP-1 or a more comprehensive approach, <b>you’ll always start with a real clinician</b>. They evaluate your full health history, lifestyle, and past weight-loss attempts to build a plan designed for long-term health.',
        primaryCta: { label: 'Book a visit', href: '/visit' },
    },
    contentAccordion: {
        title: 'At General Medicine, you get a personalized care plan from a certified clinician.',
        subtitle:
            'Whether you want access to a GLP-1 or a more comprehensive approach, you’ll always start with a real clinician. They evaluate your full health history, lifestyle, and past weight-loss attempts to build a plan designed for long-term health.',
        images: [WhyImg2, WhyImg1],
        accordionItems: [
            {
                title: 'Share your health story',
                description:
                    '<p>Complete a brief medical questionnaire so our clinicians can understand your goals, history, and unique challenges.</p>',
            },
            {
                title: 'Meet your clinician',
                description:
                    '<p>Book a virtual weight-loss visit with a licensed physician who evaluates your needs and determines if medication is right for you. <strong>It’s covered by insurance.</strong></p>',
            },
            {
                title: 'Get your care plan',
                description:
                    '<p>If eligible, your clinician will prescribe the most appropriate treatment—GLP-1 or non-GLP-1. They’ll also help you manage related conditions such as hypertension or metabolic syndrome.</p>',
            },
            {
                title: 'Start seeing results',
                description:
                    '<p>Track your progress, schedule follow-up visits anytime, and adjust your treatment as your body evolves.</p>',
            },
        ],
    },
    tabbedCardSlider: {
        title: 'A full suite of weight-loss solutions, tailored to you',
        description:
            'No single treatment works for everyone. Your care journey starts with a conversation with your General Medicine clinician, who will help you choose the safest, most effective option based on your medical profile.',
        showHeaderInfo: true,
    },
    multiVideoPlayer: {
        title: 'Your questions, answered by our clinicians.',
        subtitle:
            '<p><b>Our clinical experts are dedicated to your long-term health.</b> Hear from General Medicine clinician, Aaron Leong, MD, MSC, about how modern weight-loss medications and individualized care plans can create meaningful, sustainable change.</p>',
        bgImage: WhyImage,
        playIconSvg: [
            'M9.33333 17.6667C13.9357 17.6667 17.6667 13.9357 17.6667 9.33333C17.6667 4.73096 13.9357 1 9.33333 1C4.73096 1 1 4.73096 1 9.33333C1 13.9357 4.73096 17.6667 9.33333 17.6667Z',
            'M7.25 6.80444C7.25 6.40671 7.25 6.20784 7.33312 6.09681C7.40555 6.00006 7.51643 5.93953 7.63698 5.93092C7.77532 5.92104 7.9426 6.02858 8.27717 6.24366L12.211 8.77255C12.5013 8.95918 12.6465 9.0525 12.6966 9.17116C12.7404 9.27484 12.7404 9.39183 12.6966 9.49551C12.6465 9.61417 12.5013 9.70748 12.211 9.89412L8.27717 12.423C7.9426 12.6381 7.77532 12.7456 7.63698 12.7357C7.51643 12.7271 7.40555 12.6666 7.33312 12.5699C7.25 12.4588 7.25 12.26 7.25 11.8622V6.80444Z',
        ],
        videos: [
            {
                title: 'Why medication alone isn’t enough',
                videoUrl:
                    'https://player.vimeo.com/progressive_redirect/playback/1084442319/rendition/1080p/file.mp4?loc=external&signature=85272d3fcfba01d06a19c6337a925405d2efb86fb0e022b31d8b4446bce093a8',
            },
            {
                title: 'When GLP-1s are powerful—and when they’re not',
                videoUrl:
                    'https://player.vimeo.com/progressive_redirect/playback/1084442319/rendition/1080p/file.mp4?loc=external&signature=85272d3fcfba01d06a19c6337a925405d2efb86fb0e022b31d8b4446bce093a8',
            },
            {
                title: 'Understanding root causes and downstream risks',
                videoUrl:
                    'https://player.vimeo.com/progressive_redirect/playback/1084442319/rendition/1080p/file.mp4?loc=external&signature=85272d3fcfba01d06a19c6337a925405d2efb86fb0e022b31d8b4446bce093a8',
            },
        ],
    },
    categoryStats: {
        variant: 'large',
        megaStat: '83%',
        image: CategoryStatsImg,
        imageAlt: 'Weight loss success',
        description:
            '83% of our customers feel more in control of their health after meeting with a General Medicine clinician.',
        stats: [
            {
                value: '20%',
                content: 'Up to 15%-20% average body weight reduction with GLP-1 treatments like Zepbound® and Wegovy®',
            },
            {
                value: '10%',
                content: 'Up to 5%-10% average weight loss with non-GLP-1 medications such as Contrave® or Saxenda®',
            },
        ],
        cta: { label: 'Book a visit', href: '/visit' },
    },
    faq: {
        title: 'Frequently asked questions about weight loss.',
        items: [
            {
                question: 'Am I eligible for GLP-1 medications?',
                answer: "Eligibility depends on your health profile, medical history, and weight-related goals. During your visit, your clinician reviews factors like BMI, metabolic markers, past treatments, and any existing conditions to determine whether a GLP-1 is appropriate and safe for you. Not everyone needs a GLP-1, and if it's not the best fit, we’ll recommend alternatives that still support meaningful weight loss.",
            },
            {
                question: 'Do I need lab work before starting?',
                answer: 'Most people can begin treatment without extensive testing, but your clinician might recommend labs if they could help guide your care—such as evaluating thyroid function, blood sugar, or hormone levels. If labs are needed, we’ll explain why and help you complete them quickly and conveniently.',
            },
            {
                question: 'How do follow-up visits work?',
                answer: 'Follow-ups are fully virtual and designed to fit your schedule. Your clinician checks in on your progress, adjusts your dosage if needed, discusses any side effects, and personalizes your plan over time. These visits ensure your treatment remains safe, effective, and aligned with your goals as your body responds.',
            },
            {
                question: 'Are these medications safe long-term?',
                answer: 'GLP-1 and non-GLP-1 medications have been widely studied and shown to be safe for ongoing use when monitored by a clinician. Like any treatment, they may have side effects, which your clinician will walk you through. We track your progress closely and adjust your plan to ensure long-term safety and sustainability.',
            },
            {
                question: 'Will insurance cover my treatment?',
                answer: 'Coverage varies based on your insurance plan, medication type, and eligibility criteria. Some members receive partial or full coverage, while others may use cash-pay options. During your pricing check, we’ll help you understand what your insurance may or may not cover, and we’ll always provide transparent cost information upfront..',
            },
            {
                question: 'What if this medication isn’t right for me?',
                answer: 'If you’re not responding well or experiencing side effects, your clinician will recommend adjustments or explore alternative treatments—GLP-1 or non-GLP-1—based on your medical needs. You’re never locked into a single option; your plan evolves with you.',
            },
            {
                question: 'Can I switch treatments later?',
                answer: 'Yes. Weight-loss care isn’t one-size-fits-all, and your needs may change over time. Your clinician can guide you through transitioning to a different medication, adjusting dosing, or exploring non-GLP-1 options to ensure the treatment continues to support your goals safely and effectively.',
            },
            {
                question: 'What are the side effects?',
                answer: 'GLP-1 medications may cause nausea, digestive changes, or decreased appetite. Non-GLP-1s may have different profiles. We walk you through every option and monitor you closely.',
            },
            {
                question: 'How fast will I see results?',
                answer: 'Most members notice changes within the first 4–8 weeks, with continued progress over time.',
            },
        ],
    },
}
